import type { CSSProperties, ReactNode } from 'react';
import { useInView } from '@/hooks/useInView';

type RevealVariant = 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right';

type RevealProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  variant?: RevealVariant;
};

const hiddenByVariant: Record<RevealVariant, string> = {
  'fade-up': 'opacity-0 translate-y-10',
  'fade-in': 'opacity-0',
  'slide-left': 'opacity-0 translate-x-12',
  'slide-right': 'opacity-0 -translate-x-12',
};

const visibleByVariant: Record<RevealVariant, string> = {
  'fade-up': 'opacity-100 translate-y-0',
  'fade-in': 'opacity-100',
  'slide-left': 'opacity-100 translate-x-0',
  'slide-right': 'opacity-100 translate-x-0',
};

export default function Reveal({
  children,
  className = '',
  delay = 0,
  variant = 'fade-up',
}: RevealProps) {
  const { ref, inView } = useInView<HTMLDivElement>();
  const style = { transitionDelay: `${delay}ms` } satisfies CSSProperties;

  return (
    <div
      ref={ref}
      style={style}
      className={`transition-all duration-700 ease-out will-change-transform ${
        inView ? visibleByVariant[variant] : hiddenByVariant[variant]
      } ${className}`}
    >
      {children}
    </div>
  );
}
